/* eslint-disable @faire/enforce-internal-folder-encapsulation */
import {
  categoryRouteMatcher,
  getCategoryRouteMatches,
} from "@faire/retailer-visitor-shared/app/_lib/templates/head/prefetches/__internal__/catNavPrefetches/catNavRouteInfo";
// eslintstrictLocalize, -disable-next-line @faire/enforce-internal-folder-encapsulation
import { fromUrlIds } from "@faire/retailer-visitor-shared/models/Category/fromUrlIds";
import { useCategoryStore } from "@faire/retailer-visitor-shared/stores/domain/useCategoryStore";
import { isNotUndefined } from "@faire/typescript";
import { useStrictLocalization } from "@faire/web/common/localization";
import { RetailerRoute } from "@faire/web/common/routes/RetailerRouteMatcher";
import { useQuery_escape_hatch as useRelatedSearchTermQuery } from "@faire/web-api--source/endpoints/www/api/v2/search/cat-nav/related-search-term/post-hooks";
import { ICategoryRelatedSearchTermRequest } from "@faire/web-api--source/indigofair/data/ICategoryRelatedSearchTermRequest";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

//This is used for logged out discover and search pages
export const useLoggedOutCategoryTopSearchPlaceholder = (
  pathIdentifier: string | undefined
) => {
  const { strictLocalize } = useStrictLocalization();
  const fallbackPlaceholder = strictLocalize({
    defaultMessage: "Search wholesale products or brands",
    description: {
      text: "Text for search box.",
    },
  });

  const { data, isPending } = useRelatedSearchTermQuery(
    ICategoryRelatedSearchTermRequest.build({
      category_path_identifier: pathIdentifier,
    }),
    {
      enabled: !!pathIdentifier,
    }
  );

  const searchTerm = data?.related_search_term;

  const placeholder = useMemo(() => {
    if (isPending) {
      return undefined;
    }

    if (searchTerm) {
      return strictLocalize(
        {
          defaultMessage: "Search for “{searchProductName}”",
          description: {
            text: "Suggested search term for the search bar",
          },
        },
        {
          searchProductName: searchTerm ?? "",
        }
      );
    } else {
      return fallbackPlaceholder;
    }
  }, [fallbackPlaceholder, isPending, searchTerm, strictLocalize]);

  return placeholder;
};

const getCategoryRouteMatchesWrapper = (
  route: RetailerRoute | undefined,
  match: string[] | undefined
) => {
  if (!route || !match) {
    return {
      c1CategoryUrlId: undefined,
      c2CategoryUrlId: undefined,
      c3CategoryUrlId: undefined,
      c4CategoryUrlId: undefined,
      c5CategoryUrlId: undefined,
      c6CategoryUrlId: undefined,
    };
  }
  return getCategoryRouteMatches(route, match);
};

export const useGetCategoryIdentifier = (): string | undefined => {
  const location = useLocation();
  const matchedRoute = categoryRouteMatcher.match(location.pathname);
  const { categories } = useCategoryStore();

  const {
    c1CategoryUrlId,
    c2CategoryUrlId,
    c3CategoryUrlId,
    c4CategoryUrlId,
    c5CategoryUrlId,
    c6CategoryUrlId,
  } = getCategoryRouteMatchesWrapper(matchedRoute?.route, matchedRoute?.match);
  const urlIds = [
    c1CategoryUrlId,
    c2CategoryUrlId,
    c3CategoryUrlId,
    c4CategoryUrlId,
    c5CategoryUrlId,
    c6CategoryUrlId,
  ].filter(isNotUndefined);

  const category = fromUrlIds(urlIds, categories ?? []);

  return category?.mostSpecificPathIdentifier;
};
